import { useState, useEffect, useRef } from "react";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { numberToWords } from "../../../../utils/numberToWords";
import sortBy from "lodash/sortBy";
import dayjs from "dayjs";
import db from "../../../../database";
import upsertPOSLog from "../../Retail/posLog";
import useCartStore from "../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useConfigStore from "../../../../stores/useConfigStore";
import useSessionStore from "../../../../stores/useSessionStore";
import { logSequenceEvent } from "../../../../utils/sequenceAudit";
import { isStalePersistedCart } from "../../../../utils/cartIdentityGuard";
import { runStartupReconcilePass } from "../../../../utils/startupSequenceReconcile";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import { posTillUser } from "../../../../utils/posSession";
import { getCoreRoundOffTender } from "../../../../utils/paymentRounding";

// BUG-2468: while the server cannot be reached at POS mount, retry the startup
// reconcile on this interval (and on the browser's "online" event) until one pass succeeds.
const STARTUP_RECONCILE_RETRY_MS = 30000;

// How long a freshly-retrieved/completed layaway cart's justRetrievedAt stamp is
// trusted by clearStaleCart's mount-time check below, before falling back to the
// db.orders-based inference. See the comment at that check for why the stamp
// exists at all.
const JUST_RETRIEVED_WINDOW_MS = 10000;

/**
 * useCoreEffects
 *
 * Extracts large useEffect blocks and notes-related handlers from PointOfsaleCore:
 * - Cart total / amountInWords effect
 * - Sale type sync effect
 * - Pre-order cart check effect
 * - Cart payment methods sync effect
 * - Store-credit wallet auto-select effect
 * - Auto-process payment effect
 * - Notes handlers (onAddNotes, handleKeyDown, onChangeInputKeyboard, onChange, handleShift)
 * - Keyboard handler effects (activeInput focus, keydown listener)
 * - Product stock timer effect
 * - Currency fill + sale types init effect
 * - Hold queue localStorage sync effect
 */
const useCoreEffects = ({
  cart, setCart, isExchange, precision, tillData, tillLayout, posConfig,
  history, tillaccess,
  // Notes
  notesPopup, notesValue, setNotesValue, exchangeNotesValue, setExchangeNotesValue, activeCartTable, setActiveCartTable, selectedProductForNotes, setSelectedProductForNotes,
  setNotesPopup, setIsInputFocused, keyboardRef, layoutName, setLayoutName,
  // Cart effects
  setOverPayedAmount, setSelectedProductInCart, setGroupQty,
  showPaymentMethods, setShowPaymentMethods, setPaymentProcessFlag,
  getFilteredPaymentMethods, setShowPayments, setStartIndex,
  amount, selectedPaymentMethod, isNewGiftCardFlag, showCustomerWallet,
  processOrder,
  // Active input
  activeInput, searchInputRef, qtyInputRef,
  // Product stock
  productStock, setProductStock,
  // Currency + sale types
  currenciesData, setCurrenciesData, setPosSaleTypes, setOrderType,
  // Hold queue
  holdQueue,
  // Active tab
  activeTabName, setActiveTabName, retailCustomerFields,
  // Selected sale type
  selectedSaleType,
  // Wallet
  isRefundWalletOpen, setSelectedPaymentMethod,
  // Cart amount/order
  setAmount, setOrderObj,
  // Zone navigation
  suppressAutoFocusRef,
  // BUG-2468: Core's checkout reads this React copy of the counter, not the store
  setDocumnetSequence,
}) => {
  const orderState = useRef(0);
  const isInitialMount = useRef(true);
  // Last (sOrderID, paid) this effect itself auto-fired processOrder for. "Fully
  // paid" is a standing state for an advance/layaway order (true from the moment
  // it's first created), not a one-time event -- without this, any cart update
  // that merely re-observes an already-satisfied total<=paid condition (a stale
  // reload, a re-retrieve, anything) re-fires processOrder on data this effect
  // already acted on, silently clobbering whatever was saved since. Gate on an
  // actual change in `paid` for this exact order, not on the condition still
  // being true. See docs/chocolala-advance-edit-resubmit-lost-update-rca.html.
  const lastAutoProcessed = useRef({ sOrderID: null, paid: null });
  // BUG-2020: a retrieved Advance order (cart.preOrder === "Y") used to be fully
  // locked from editing, so `total <= paid` could only become true via an actual
  // new payment. Cart editing is now allowed on these orders (item add/remove),
  // which can also drive total down to <=paid on its own -- e.g. removing a line
  // -- and that must NOT auto-complete the sale; only a genuine new payment should.
  // Tracks the previous render's cart.paid so the auto-process branches below can
  // require paid to have actually increased for this specific order type.
  const prevPaidForOrder = useRef({ sOrderID: null, paid: null });

  // Notes handlers
  const onAddNotes = () => {
    let updatedCart;
    const activeCart = activeCartTable === "EXCHANGE" ? useCartStore.getState().exchangeCart : cart;
    if (selectedProductForNotes !== null && activeCart.items.length > 0) {
      updatedCart = { ...activeCart };
      let index = updatedCart.items.findIndex((res) => res.lineId === selectedProductForNotes.lineId);
      if (index >= 0) {
      updatedCart.items[index].notes = activeCartTable === "EXCHANGE" ? exchangeNotesValue : notesValue;
      }
    if (activeCartTable === "EXCHANGE") {
      setExchangeNotesValue("");
    } else {
      setNotesValue("");
    }
    } else {
    updatedCart = {
      ...activeCart,
      description:
        activeCartTable === "EXCHANGE"
          ? exchangeNotesValue
          : notesValue,
    };
    }
    setSelectedProductForNotes(null);
    if (activeCartTable === "EXCHANGE") {
      useCartStore.getState().setExchangeCart(updatedCart);
    } else {
      setCart(updatedCart);
    }
    setNotesPopup(false);
    setIsInputFocused(false);
    let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
    if (updatedCart.items.length === 0 || tillData.tillAccess.csBunit.b2cCustomer.cwrCustomerId === updatedCart.customer.cwrCustomerId) {
      orderTimeDetails = {
        ...orderTimeDetails,
        paymentStartTime: "",
        orderStartTime: dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"),
      };
    } else if (updatedCart.items.length > 0 || tillData.tillAccess.csBunit.b2cCustomer.cwrCustomerId !== updatedCart.customer.cwrCustomerId) {
      orderTimeDetails = {
        ...orderTimeDetails,
        paymentStartTime: "",
      };
    }
    if (updatedCart.description === "") {
      orderTimeDetails = {
        ...orderTimeDetails,
        paymentStartTime: "",
        orderStartTime: "",
      };
    }
    usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAddNotes();
    }
  };

  const onChangeInputKeyboard = (event) => {
    const input = event.target.value;

    if (activeCartTable === "EXCHANGE") {
      setExchangeNotesValue(input);
    } else {
      setNotesValue(input);
    }
    if (keyboardRef.current) {
      keyboardRef.current.setInput(input);
    }
  };

  const onChange = (input) => {
    if (input !== "Enter") {
    if (activeCartTable === "EXCHANGE") {
      setExchangeNotesValue(input);
    } else {
      setNotesValue(input);
    }
    }
  };

  const handleShift = () => {
    layoutName === "default" ? setLayoutName("shift") : setLayoutName("default");
  };

  // On POS mount: detect a stale persisted cart whose order was already saved to
  // db.orders. This covers the crash/close scenario where db.orders.put() succeeded
  // but the app was killed before deleteCart() ran, leaving completed-order items
  // in the SQLite-backed Zustand store.
  useEffect(() => {
    const clearStaleCart = async () => {
      const persistedCart = useCartStore.getState().cart;
      if (!persistedCart.items?.length || !persistedCart.sOrderID) return;
      // A cart just handed off by a legitimate retrieve/complete-sale flow (Manage
      // Pre-Orders' Complete Sale, Parked Bills' Retrieve) carries a fresh
      // justRetrievedAt stamp. Trust that instead of inferring "still open" from
      // the matching db.orders row's documentno shape below — that inference is
      // wrong on any till without a configured preorderSequence prefix, since a
      // pre-order there gets its real till-sequence documentno at creation and
      // never carries a parked-series placeholder, so isParkedSeriesDocumentno
      // can never confirm "still parked" for it (KKHPOS/17739, till has
      // cwrTill.preorderSequence: null — every Complete Sale was being wiped here
      // ~130ms after navigating to /pos). A genuinely stale post-crash reload
      // never carries a stamp this fresh — real elapsed time always exceeds the
      // window by the time the app is reopened.
      if (persistedCart.justRetrievedAt && Date.now() - persistedCart.justRetrievedAt < JUST_RETRIEVED_WINDOW_MS) return;
      try {
        const existingOrder = await db.orders.get(persistedCart.sOrderID);
        // Layaway / confirmed-restaurant carts intentionally share their sOrderID
        // with a db.orders row; retrieved carts do too, but only while that row
        // still carries its parked-series (PB//ADV) documentno — a retrieved cart
        // whose row already holds a regular bill number is a stale snapshot of a
        // completed sale and must be cleared like any other.
        const preorderPrefix = useSessionStore.getState().preorderSequence?.prefix;
        if (isStalePersistedCart({ cart: persistedCart, existingOrder, preorderPrefix })) {
          console.warn("[BUG-1477][startup] STALE CART DETECTED — order already saved in db.orders. Clearing cart now.", {
            sOrderID: persistedCart.sOrderID,
            documentno: existingOrder.documentno,
            itemCount: persistedCart.items.length,
          });
          logSequenceEvent(
            "stale-identity",
            {
              staleSOrderID: persistedCart.sOrderID,
              existingDocumentno: existingOrder.documentno,
              itemCount: persistedCart.items.length,
              flow: "pos-mount",
            },
            db
          );
          useCartStore.getState().clearCart();
        } else if (
          existingOrder &&
          !persistedCart.retrievedDocumentno &&
          (persistedCart.layAway === "Y" || persistedCart.type === "Layaway" || persistedCart.docType === "Advance" || persistedCart.isRetrived === "Y")
        ) {
          // BUG-2338 (RC2): this cart is genuinely still open (isStalePersistedCart
          // said so above) but reached this state by surviving a reload/crash, not
          // through one of the 5 explicit "Retrieve" UI actions that stamp
          // retrievedDocumentno (selectParkedBill/selectLayAwayOrder/
          // selectCompleteLayAwayOrder/handleRetrieve/handleRetrieveEstimate — see
          // cartIdentityGuard.js). Left unstamped, it carries the identical
          // unconditional-regeneration exposure those 5 fixes closed, the next time
          // this cart is saved/completed — with no stale build or missed click
          // required, just a routine refresh mid-order. Backfill the same snapshot
          // those entry points would have captured, using this row's own current
          // documentno (matches every other stamp site's pattern). Scoped to
          // existingOrder present + a Layaway/Advance/retrieved cart, same as
          // isStalePersistedCart's own branches above — a plain in-progress cart
          // with no matching db.orders row yet (the common case) never enters here.
          useCartStore.getState().setCart((prev) => (prev.sOrderID === persistedCart.sOrderID ? { ...prev, retrievedDocumentno: existingOrder.documentno } : prev));
        }
      } catch (err) {
        console.warn("[BUG-1477][startup] Stale cart check failed (non-blocking):", err?.message);
      }
    };

    // BUG-2468: the check above only sees db.orders. After a power cut / crash the
    // browser can restore db.orders, the counter and the cart together from before the
    // last bills, so it finds nothing. Reconcile the counter and the restored cart's
    // identity against the server on every mount (login already does this; a reload,
    // Full Sync reload or restart did not). Retries until the server answers once.
    let cancelled = false;
    let retryTimer = null;
    const authHeaders = getOAuthHeaders?.() || {};
    const userData = posTillUser(authHeaders);
    const onCartCleared = (documentno) => {
      Modal.warning({
        title: "Previous bill already saved",
        content: `Bill ${documentno} was already saved before the POS restarted. The cart has been cleared — check the last printed bill before billing again.`,
      });
    };
    // An unpaid cart whose number the server already used is moved to the next number
    // silently (no popup); the stale-identity audit event still records old → new.
    const onCartRenumbered = (oldNo, newNo) => {
      console.info("[BUG-2468][startup] Cart moved to the next bill number:", { oldNo, newNo });
    };
    const runReconcile = async () => {
      if (cancelled) return true;
      try {
        return await runStartupReconcilePass({
          db,
          cartStore: useCartStore,
          sessionStore: useSessionStore,
          tillData,
          userData,
          setReactSequence: setDocumnetSequence,
          onCartCleared,
          onCartRenumbered,
          // Restaurant carts are bound to table sessions/KOTs — counter only.
          cartChecks: tillLayout !== 2,
        });
      } catch (err) {
        console.warn("[BUG-2468][startup] Sequence reconcile failed (non-blocking):", err?.message);
        return false;
      }
    };
    const stopRetry = () => {
      if (retryTimer) clearInterval(retryTimer);
      retryTimer = null;
      window.removeEventListener("online", onOnline);
    };
    const retryOnce = async () => {
      if (await runReconcile()) stopRetry();
    };
    const onOnline = () => {
      retryOnce();
    };

    (async () => {
      await clearStaleCart();
      if (cancelled) return;
      const reached = await runReconcile();
      if (!reached && !cancelled) {
        retryTimer = setInterval(retryOnce, STARTUP_RECONCILE_RETRY_MS);
        window.addEventListener("online", onOnline);
      }
    })();

    return () => {
      cancelled = true;
      stopRetry();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Active tab name default
  useEffect(() => {
    if (activeTabName === "") {
      setActiveTabName("contact");
    }
  }, [retailCustomerFields]);

  // Notes popup focus
  useEffect(() => {
    const notesValueInput = document.getElementById("notes-input");
    if (notesPopup) {
      notesValueInput?.focus();
    } else {
      notesValueInput?.blur();
      setIsInputFocused(false);
    }
  }, [notesPopup]);

  // Hold queue localStorage sync
  useEffect(() => {
    localStorage.setItem("callCenterHoldQueue", JSON.stringify(holdQueue));
  }, [holdQueue]);

  // Active input focus + keydown listener
  useEffect(() => {
    const handleKeyDownGlobal = (event) => {
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA" || activeElement.isContentEditable);
      // BUG-1185: Also detect weight/UOM popups that use getContainer={false} (renders inside parent, not body)
      const isDrawerOrModalOpen = !!document.querySelector(".ant-drawer-open") || !!document.querySelector(".ant-modal-mask") || document.body.classList.contains("ant-modal-open") || !!document.querySelector(".ant-modal-wrap:not([style*='display: none'])");
      if (!isInputFocused && !isDrawerOrModalOpen && !event.ctrlKey && !event.altKey && !event.metaKey && !suppressAutoFocusRef?.current) {
        if (event.key.length === 1 || event.key === "Backspace" || event.key === "Delete" || event.key === "ArrowLeft" || event.key === "ArrowRight") {
          if (typeof setActiveInput === "function") setActiveInput("search");
          if (searchInputRef?.current) {
            searchInputRef?.current?.focus();
          }
        }
      }
    };

    if (activeInput === "search") {
      if (searchInputRef?.current) {
        searchInputRef?.current?.focus();
      }
    } else if (activeInput === "weightPopup") {
      const productSearchInput = document.getElementById("sm-weight-popup-input");
      if (productSearchInput) {
        productSearchInput.focus();
      }
    } else if (activeInput === "qty") {
      qtyInputRef.current?.focus();
    }

    document.addEventListener("keydown", handleKeyDownGlobal);
    return () => {
      document.removeEventListener("keydown", handleKeyDownGlobal);
    };
  }, [activeInput]);

  // Product stock timer
  useEffect(() => {
    if (productStock) {
      setProductStock(true);
      const timer = setTimeout(() => {
        setProductStock(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [productStock]);

  // Currency fill + sale types init
  useEffect(() => {
    const totalRecords = 12;
    let filledCurrencies = [...currenciesData];
    while (filledCurrencies.length < totalRecords) {
      filledCurrencies.push({ id: `placeholder-${filledCurrencies.length}`, value: 999999 });
    }
    setCurrenciesData(filledCurrencies.sort((a, b) => b.value - a.value));
    useConfigStore.getState().loadConfig();
    db.posSaletypes.toArray().then((saleType) => {
      setPosSaleTypes([...saleType]);
      const saleIndex = saleType.findIndex((st) => st.cwrSaletype.isdefault === "Y");
      setOrderType(saleType[saleIndex]);
    });
  }, []);

  // Cart total / amountInWords effect
  const precisionGuardRef = useRef(0);
  useEffect(() => {
    if (cart?.items?.length > 0) {
      const preciseTotal = parseFloat(cart?.total?.toFixed(precision));
      if (preciseTotal !== cart?.total && Math.abs(preciseTotal - cart?.total) > 1e-10) {
        if (precisionGuardRef.current >= 3) {
          precisionGuardRef.current = 0;
          return;
        }
        precisionGuardRef.current += 1;
        let updatedCart = {
          ...cart,
          total: preciseTotal,
          amountInWords: numberToWords(preciseTotal),
          totalStd:
            cart?.items?.reduce((sum, item) => {
              if (item?.isCanceled === "Y") return sum;
              if (Array.isArray(item.children) && item.children.length > 0) {
                const childrenStd = item.children.reduce((childSum, child) => childSum + (child?.linestd || 0), 0);
                return sum + childrenStd;
              }
              return sum + (item?.linestd || 0);
            }, 0) || 0,
        };
        setCart(updatedCart);
      } else {
        precisionGuardRef.current = 0;
      }
    } else {
      precisionGuardRef.current = 0;
    }
    // Exchange cart precision
    const exchangeCartObj = useCartStore.getState().exchangeCart;
    if (isExchange && exchangeCartObj?.items?.length > 0) {
      const exchPreciseTotal = parseFloat(exchangeCartObj?.total?.toFixed(precision));
      if (exchPreciseTotal !== exchangeCartObj?.total) {
        const updatedExchCart = {
          ...exchangeCartObj,
          total: exchPreciseTotal,
          amountInWords: numberToWords(exchPreciseTotal),
          totalStd:
            exchangeCartObj?.items?.reduce((sum, item) => {
              if (item?.isCanceled === "Y") return sum;
              if (Array.isArray(item.children) && item.children.length > 0) {
                const childrenStd = item.children.reduce((childSum, child) => childSum + (child?.linestd || 0), 0);
                return sum + childrenStd;
              }
              return sum + (item?.linestd || 0);
            }, 0) || 0,
        };
        localStorage.setItem("exchangeCartObj", JSON.stringify(updatedExchCart));
        useCartStore.getState().setExchangeCart(updatedExchCart);
      }
    }
  }, [cart, isExchange]);

  // Sale type sync effect
  useEffect(() => {
    let saleType = usePOSConfigStore.getState().saleTypeData;
    let cartObj = useCartStore.getState().cart || cart;
    if (saleType && cartObj?.items?.length > 0) {
      let updatedCart = {
        ...cartObj,
        saleType: saleType?.cwrSaletype,
      };
    }
  }, [selectedSaleType, cart]);

  // Pre-order cart check effect
  useEffect(() => {
    if (cart?.items?.length > 0) {
      if (window.location.pathname.includes("/pos")) {
        if (cart?.isPreOrderPage === true && cart?.preOrder !== "Y" && tillaccess?.layout !== "2") {
          Modal.confirm({
            title: "Clear The Cart Items ?",
            icon: <ExclamationCircleOutlined />,
            content: <div>Pre-Order cart will be cleared if you continue. Do you want to proceed?</div>,
            okText: "Yes",
            cancelText: "No",
            autoFocusButton: null,
            okButtonProps: { style: { display: "inline-block" } },
            onOk() {
              let updatedCart = {
                ...cart,
                isPreOrderPage: false,
                items: [],
                total: 0,
                subTotal: 0,
                tax: 0,
                discount: 0,
                paid: 0,
                change: 0,
                roundOff: 0,
                advancePayment: 0,
                couponInput: [],
                payments: [],
                giftCardRefId: null,
                couponRefId: null,
                itemsSummery: {},
                groupedItemsByTaxRate: {},
              };
              if (!isExchange) {
                setCart(updatedCart);
              }
            },
            onCancel() {
              // BUG-1916 (8d4dec37) proved this exact pattern: a /preorder push on a
              // till without PreOrder access gets instantly rejected by ModuleGate and
              // bounced back to /pos, unmounting+remounting PointOfsaleCoreGuard's
              // whole subtree — visible as the billing screen blinking. This call site
              // stamps cart.isPreOrderPage from a *persisted* cart (useCartStore is
              // rehydrated globally per device, not per-till — see Till.js), so it can
              // still be true on a later till/session where enablePreOrder isn't "Y" —
              // gate it the same way the other /preorder push sites already are.
              if (posConfig?.enablePreOrder === "Y") {
                history.push("/preorder");
              } else {
                // BUG-2097: don't just skip the push — this effect depends on the whole
                // `cart` object, so a stale isPreOrderPage flag that's never cleared
                // reopens this same modal on every subsequent cart mutation. Drop only
                // the flag; leave items/discounts/payments untouched. Safe for the
                // legitimate Restaurant-Preorder flow, which stamps isPreOrderPage
                // together with preOrder:"Y" and never reaches this branch (guarded by
                // cart?.preOrder !== "Y" above).
                setCart({ ...cart, isPreOrderPage: false });
              }
            },
          });
        }
      } else if (window.location.pathname.includes("/preorder") && !cart.isPreOrderPage) {
        let updatedCart = {
          ...cart,
          isPreOrderPage: true,
        };
      }
    }
  }, [cart]);

  // Cart payment methods sync effect
  useEffect(() => {
    let cartObj = useCartStore.getState().cart || cart;
    // cartRef.current = { ...cart }; — done in caller
    if (cart.items.length === 0) {
      setOverPayedAmount(0);
      setSelectedProductInCart({});
      setGroupQty(1);
      setShowPaymentMethods(false);
      // Only call setCart if there's actually something to reset (avoid infinite loop)
      if (cart.couponInput?.length > 0 || cart.discount > 0 || cart.payments?.length > 0 || cart.paid > 0 || cart.giftCardRefId || cart.totalBillDicount) {
        // F-07: also clear paymentReferenceData — spreading ...cart otherwise carries
        // any stale reference data (cheque number, UTR, card last-4) into the next bill.
        setCart({ ...cart, couponInput: [], discount: 0, payments: [], paid: 0, giftCardRefId: null, totalBillDicount: null, paymentReferenceData: {} });
      }
    }
    let PaymentMethods = getFilteredPaymentMethods() || [];
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    const advanceItem = cart.items.find((item) => item.type === "S" && item.weight > 0 && item.productSegment === "AD");
    if (
      cart.layAway === "Y" &&
      cart?.docType === "Advance" &&
      !advanceItem &&
      posConfig?.allowLayawaywithoutPayment !== "Y" &&
      cart.items.some((item) => item.type !== "S" && item?.productSegment !== "AD")
    ) {
      PaymentMethods = [
        ...PaymentMethods,
        ...(posConfig?.enablePreOrder !== "Y"
          ? [
              {
                cWRPaymentMethodID: "",
                sequenceNo: 9999,
                finPaymentmethodId: "",
                finFinancialAccountId: "",
                finDayCloseAccountId: null,
                name: "Complete Order",
                integratedPayment: false,
                isloyalty: false,
                paymentProvider: null,
                iscredit: false,
                isGiftCard: false,
                isDefault: "N",
                csCurrencyId: "",
                isoCode: "AED",
                isactive: true,
                salesType: [],
              },
            ]
          : []),
      ];
      let payments =
        PaymentMethods.length > 5
          ? sortBy(PaymentMethods, "sequenceNo").slice(
              0,
              tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
            )
          : PaymentMethods;
      setShowPayments(payments);
      setStartIndex((cart.layAway === "Y" || cart?.creditSale === "Y") && payments.length >= 5 ? 5 : payments.length);
    } else if (isPreOrderPage) {
      const isRefundScenario = amount < 0 && selectedPaymentMethod?.name?.toLowerCase() === "cash";
      const maxPaymentsToShow = isRefundScenario ? 3 : 4;
      let payments =
        PaymentMethods.length > maxPaymentsToShow
          ? sortBy(PaymentMethods, "sequenceNo").slice(
              0,
              tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === maxPaymentsToShow
                ? maxPaymentsToShow
                : cart.layAway === "Y"
                  ? maxPaymentsToShow
                  : maxPaymentsToShow
            )
          : PaymentMethods;
      setShowPayments(payments);
      setStartIndex(cart.layAway === "Y" && payments.length >= maxPaymentsToShow ? maxPaymentsToShow : payments.length);
    } else if (!showPaymentMethods) {
      let payments =
        PaymentMethods.length > 5
          ? sortBy(PaymentMethods, "sequenceNo").slice(
              0,
              tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
            )
          : PaymentMethods;
      setShowPayments(payments);
      setStartIndex((cart.layAway === "Y" || cart?.creditSale === "Y") && payments.length >= 5 ? 5 : payments.length);
    }
  }, [cart, selectedSaleType]);

  // Store-credit wallet auto-select effect
  const selectedReturnTypeReactive = useTransactionStore((s) => s.selectedReturnType);
  useEffect(() => {
    const cartOBJ = useCartStore.getState().cart;
    if (selectedReturnTypeReactive === "STORE_CREDIT" && cartOBJ?.customer?.cwrWallet?.wallet_Id) {
      const walletPayment = tillData.tillAccess.csBunit.paymentMethodList.find((pm) => pm.isWallet === "Y");
      if (walletPayment) {
        setSelectedPaymentMethod(walletPayment);
      }
    }
  }, [cart, isRefundWalletOpen, selectedReturnTypeReactive]);

  // Auto-process payment / cart amount effect
  useEffect(() => {
    const isExchangeMode = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    const exchangeCartObj = isExchangeMode ? (useCartStore.getState().exchangeCart || {}) : null;
    const baseTotal = isExchangeMode
      ? Math.abs((exchangeCartObj.total || 0) + cart.total)
      : Math.abs(cart.total);
    const remaining = parseFloat(
      (baseTotal - (cart.paid ?? 0) + (cart?.deiveryCharge ?? 0) + (cart?.tip ?? 0) + (cart?.serviceCharge ?? 0) + (cart?.packingCharge ?? 0)).toFixed(precision)
    );
    const isReturnPaymentActive = (cart.isReturn || isExchangeMode) && showPaymentMethods;
    if (!isReturnPaymentActive && !(cart.payments?.length > 0 && cart.isReturn)) {
      // BUG-2612: with the payment bar open and a round-off method (Cash) still
      // selected, show the same rounded amount a click on that method shows.
      const roundOff = showPaymentMethods
        ? getCoreRoundOffTender({ tillLayout, paymentMethod: selectedPaymentMethod, remaining, isReturn: cart.isReturn, isExchange: isExchangeMode, tillData, precision })
        : null;
      if (roundOff) {
        setAmount(roundOff.amount);
        setOverPayedAmount(roundOff.overPayedAmount);
      } else {
        setAmount(remaining);
      }
    }
    // Skip auto-processing on initial mount (prevents re-processing stale cart from SQLite)
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (isExchangeMode) {
      let flag = tillLayout !== 1 ? true : false;
      const exchTotal = Math.abs(exchangeCartObj?.total || 0);
      const retTotal = Math.abs(cart.total || 0);
      const exchPaid = Number(exchangeCartObj?.paid) || 0;
      const netPayable = exchTotal - retTotal - exchPaid;
      if (netPayable <= 0.01 && cart.payments?.length > 0 && flag) {
        orderState.current = 0;
        setPaymentProcessFlag(false);
        processOrder(cart.sOrderID);
      }
    } else {
      let flag = tillLayout !== 1 ? true : false;
      // "Fully paid" (Math.abs(cart.total) <= cart.paid) is a standing state for an
      // advance/layaway order, not a one-time event -- it stays true for the rest of
      // that order's life once first reached. Without alreadyHandled, any later cart
      // update that merely re-observes this already-satisfied condition (a stale
      // reload, a re-retrieve landing on old data, a duplicate render) re-fires
      // processOrder on whatever happens to be in `cart` right now, silently
      // overwriting a correct save made since. Confirmed live: this auto-fired a save
      // built from a pre-edit cart snapshot ~60s after the correct edit had already
      // been saved, reverting it (Chocolala AJ2 ADV/47). Gate on `paid` actually
      // changing for this exact order since the last time this effect acted.
      const alreadyHandled = lastAutoProcessed.current.sOrderID === cart.sOrderID && lastAutoProcessed.current.paid === cart.paid;
      // BUG-2342: processOrder returns false when its Complete-Sale stock check
      // blocks the order. Clear the marker so the same fully-paid order can
      // auto-complete once stock is available, instead of alreadyHandled
      // suppressing it until `paid` changes.
      const resetAutoProcessedIfStockBlocked = (result) => {
        if (result === false) lastAutoProcessed.current = {};
      };
      // BUG-2020: a retrieved Advance order (layAway:"Y" + preOrder:"Y") is now
      // editable, so total<=paid can become true from an item edit alone, not just
      // a new payment. Only let a genuine payment increase trigger auto-complete
      // for this order type -- see prevPaidForOrder's docblock above.
      const isRetrievedAdvanceOrder = cart.layAway === "Y" && cart.preOrder === "Y";
      const paidIncreasedForThisOrder =
        prevPaidForOrder.current.sOrderID === cart.sOrderID ? cart.paid > prevPaidForOrder.current.paid : cart.paid > 0;
      const safeFromEditDrivenAutoComplete = !isRetrievedAdvanceOrder || paidIncreasedForThisOrder;
      if (
        safeFromEditDrivenAutoComplete &&
        !alreadyHandled &&
        Math.abs(cart.total) <= cart.paid &&
        cart.payments.length > 0 &&
        flag &&
        cart.layAway === "Y" &&
        posConfig?.allowLayawaywithoutPayment === "Y"
      ) {
        lastAutoProcessed.current = { sOrderID: cart.sOrderID, paid: cart.paid };
        orderState.current = 0;
        setPaymentProcessFlag(false);
        Promise.resolve(processOrder(cart.sOrderID)).then(resetAutoProcessedIfStockBlocked);
      } else if (
        safeFromEditDrivenAutoComplete &&
        !alreadyHandled &&
        Math.abs(cart.total) <= cart.paid &&
        cart.payments.length > 0 &&
        flag &&
        (cart.layAway !== "Y" || cart.preOrder === "Y") &&
        !isNewGiftCardFlag &&
        !showCustomerWallet
      ) {
        lastAutoProcessed.current = { sOrderID: cart.sOrderID, paid: cart.paid };
        orderState.current = 0;
        setPaymentProcessFlag(false);
        Promise.resolve(processOrder(cart.sOrderID)).then(resetAutoProcessedIfStockBlocked);
      }
      prevPaidForOrder.current = { sOrderID: cart.sOrderID, paid: cart.paid };
    }
    try {
      const cartDetails = useCartStore.getState().cart;
      if (cartDetails !== null) {
        setOrderObj(cartDetails);
      }
    } catch (error) {
      console.error("Error parsing cart details:", error);
    }
    if (cart.discount) {
      upsertPOSLog(cart, "DAP");
    }
  }, [cart]);

  return {
    onAddNotes,
    handleKeyDown,
    onChangeInputKeyboard,
    onChange,
    handleShift,
    orderState,
  };
};

export default useCoreEffects;
